import moment from 'moment';
import React, { useState } from 'react';
import useIsClamped from 'ats/hooks/useIsClamped';
import UserProfileAvatar from 'ats/components/user-profile-avatar/UserProfileAvatar';
import { formatTimeAgo } from 'shared/utils';
import HoverMenu from 'ats/components/hover-menu/HoverMenu';
import PendingTaskBadge from 'ats/components/pending-task-badge/PendingTaskBadge';
import { authStore } from 'shared/security/AuthStore';
import {
	getApplicantTaskStatusIcon,
	getApplicantTaskTheme
} from 'ats/utils/applicantTaskTheme';
import { applicantStore } from '../ApplicantStore';
import LinkifyText from 'ats/components/linkify-text/LinkifyText';
import { PIN_SCOPES, isTaskPinned } from 'ats/utils/taskPin';
import { getTaskMenuItems } from 'ats/utils/taskActions';

const COMMENT_PREFIX = 'Comment:';

const renderTaskDescription = description => {
	if (description?.startsWith(COMMENT_PREFIX)) {
		return (
			<>
				<span className="task-card-description-label">{COMMENT_PREFIX}</span>
				<LinkifyText text={description.slice(COMMENT_PREFIX.length)} />
			</>
		);
	}

	return <LinkifyText text={description} />;
};

export default function TaskCard({ task, onSuccess }) {
	const [showMore, setShowMore] = useState(false);
	const [descriptionRef, isClamped] = useIsClamped(
		task?.description,
		!showMore
	);

	const getType = value => {
		return applicantStore.taskOptions.find(task => task.value === value);
	};

	const getMenuItems = task =>
		getTaskMenuItems({ scope: PIN_SCOPES.APPLICANT, task, onSuccess });

	const taskTheme = getApplicantTaskTheme(task?.type);

	return (
		<div
			data-task-id={task.id}
			className={`applicant-task-card job-activity-task ${taskTheme.className} ${isTaskPinned(task) ? 'is-pinned' : ''}`}>
			<div className="task-card-info-container">
				<div className="task-card-info">
					<div className="task-card-info-header">
						<div className="task-card-type">
							<img src={taskTheme.icon} alt="" />
							<span>{getType(task?.type)?.label}</span>
						</div>
						<div className="task-card-status">
							<img src={getApplicantTaskStatusIcon(task?.status)} alt="task" />
							<span className={`is-capitalized task-status-${task?.status}`}>
								{task?.status}
							</span>
						</div>
					</div>
					<div className="task-card-info-schedule">
						{task?.assignments?.length > 0 && (
							<div className="task-card-info-schedule-assignments">
								{task?.assignments?.map(assignment => (
									<UserProfileAvatar
										imageUrl={assignment?.imageUrl}
										name={assignment?.name}
										tooltipPosition="right"
									/>
								))}
							</div>
						)}
						<span>
							<strong>Scheduled: </strong>{' '}
							{task?.scheduledDate
								? moment(task?.scheduledDate).format('lll')
								: 'N/A'}
						</span>
					</div>
				</div>
				<div className="task-card-info actions">
					<span className="task-info-updated-by">
						<UserProfileAvatar
							imageUrl={task?.updatedBy?.imageUrl}
							name={task?.updatedBy?.name}
						/>
						{formatTimeAgo(new Date(task?.updatedAt), {
							moreThanOneDay: true
						})}
					</span>
					<HoverMenu
						id={task.id}
						position="left"
						buttonIcon={
							<img src="/ats/icons/three-dots-icon.svg" alt="Menu button" />
						}
						items={getMenuItems(task)}
					/>
					{task?.status === 'pending' &&
						task?.assignments?.some(a => a.id === authStore.profile?.id) && (
							<PendingTaskBadge count={1} variant="dot" />
						)}
				</div>
			</div>
			{task?.description && (
				<div className="task-card-description">
					<p
						ref={descriptionRef}
						className={showMore ? undefined : 'is-clamped'}>
						{renderTaskDescription(task?.description)}
					</p>
					{(showMore || isClamped) && (
						<button onClick={() => setShowMore(!showMore)}>
							{showMore ? 'View Less' : 'View More'}
						</button>
					)}
				</div>
			)}
		</div>
	);
}
